import React, { useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import { getTheme } from 'theme';

type ThemeModeValue = {
	mode: PaletteMode;
	setMode: (mode: PaletteMode) => void;
	toggleMode: () => void;
};

const ThemeModeContext = React.createContext<ThemeModeValue | undefined>(
	undefined,
);

const THEME_STORAGE_KEY = 'atlas-theme-mode';

const getInitialMode = (): PaletteMode => {
	if (typeof window === 'undefined') {
		return 'dark';
	}

	const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
	if (stored === 'light' || stored === 'dark') {
		return stored;
	}

	const prefersDark =
		window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

	return prefersDark ? 'dark' : 'light';
};

export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const [mode, setMode] = useState<PaletteMode>(getInitialMode);

	const theme = useMemo(() => getTheme(mode), [mode]);

	const toggleMode = () => {
		setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
	};

	React.useEffect(() => {
		if (typeof window !== 'undefined') {
			window.localStorage.setItem(THEME_STORAGE_KEY, mode);
			document.documentElement.setAttribute('data-theme-mode', mode);
			document.body.setAttribute('data-theme-mode', mode);
		}
	}, [mode]);

	const value = useMemo(
		() => ({
			mode,
			setMode,
			toggleMode,
		}),
		[mode],
	);

	return (
		<ThemeModeContext.Provider value={value}>
			<ThemeProvider theme={theme}>{children}</ThemeProvider>
		</ThemeModeContext.Provider>
	);
};

export const useThemeMode = (): ThemeModeValue => {
	const context = React.useContext(ThemeModeContext);

	if (!context) {
		throw new Error('useThemeMode must be used within ThemeModeProvider');
	}

	return context;
};
